import { defineStore } from 'pinia'
import router from '@/router'
import { loginUser,
    loginUserWithToken,
    confirmLoginToken,
    refreshAccessToken as refreshAccessTokenRequest,
    askForNewPassword,
    resetPassword,
    loginUserWithExternalToken } from '@/api/requests/user/login'
import { loginUserWithPasskey } from '@/api/requests/user/passkeys'

import { useAppStore } from '@/store/app'
import { useModuleStore } from '@/store/module'
import { useBrandStore } from '@/store/brand'
import { useFilterStore } from '@/store/filter'
import { useNotificationStore } from '@/store/notification'
import { useMenuStore } from '@/store/menu'
import { useUserStore } from '@/store/user'
import { LoginUserDataResponse,
    LoginUserResponseData } from '@/api/requests/authentication/authenticationResponses'
import { useVerificationStore } from '@/store/verification'
import { useLocalStorage } from '@vueuse/core'
import { WebAuthnCredentialPayload } from '@/utils/webauthn'

export const useAuthStore = defineStore('auth', {
    state: () => ({
        accessToken: useLocalStorage<string | null>('access_token', null),
        refreshToken: useLocalStorage<string | null>('refresh_token', null),
        expiresIn: useLocalStorage<number | null>('expires_in', null),
        ticketId: useLocalStorage<string | null>('ticket_id', null),
        refreshing: false,
    }),

    actions: {
        setTokens(accessToken: string | null, refreshToken: string | null, expiresIn?: number | null) {
            this.accessToken = accessToken
            this.refreshToken = refreshToken
            this.expiresIn = expiresIn
            localStorage.setItem('access_token', accessToken ?? '')
            localStorage.setItem('refresh_token', refreshToken ?? '')
            localStorage.setItem('expires_in', String(expiresIn ?? 0))
        },

        setTicket(ticketId: string | null){
            this.ticketId = ticketId
            localStorage.setItem('ticket_id', ticketId ?? '')
        },

        getAccessToken() {
            return localStorage.getItem('access_token')
        },

        getRefreshToken() {
            return localStorage.getItem('refresh_token')
        },

        getTicket() {
            return this.ticketId
        },

        applyTokenPayload(tokenPayload: { accessToken: string; refreshToken: string; expiresIn?: number; verificationUuid?: string }) {
            this.setTokens(
                tokenPayload.accessToken,
                tokenPayload.refreshToken,
                tokenPayload.expiresIn ?? null
            )

            if (tokenPayload.verificationUuid) {
                useVerificationStore().setVerificationUuid(tokenPayload.verificationUuid)
            }
        },

        isAuthenticated() {
            const token = localStorage.getItem('access_token')
            
            return !!token
        },

        async login(login: string, password: string, recaptcha: string) {
            return new Promise((resolve, reject) => {
                loginUser(login, password, recaptcha)
                    .then((response) => {
                        const tokenPayload = extractTokenPayload(response.data)

                        if (tokenPayload) {
                            this.applyTokenPayload(tokenPayload)

                        }

                        resolve(response)
                    })
                    .catch((error) => {
                        if (!error.response) useNotificationStore().setErrors(error)
                        else if (error.response.data.errors) useNotificationStore().setErrors(error.response.data.errors)
                        else if (error.response.data.message)
                            useNotificationStore().setErrors({
                                message: error.response.data.message, 
                            })
                        else useNotificationStore().setErrors(error)

                        reject(error)
                    })
            })
        },

        async loginWithExternalToken(token: string, email: string) {
            return new Promise((resolve, reject) => {
                loginUserWithExternalToken(token, email)
                    .then((response) => {
                        const tokenPayload = extractTokenPayload(response.data)

                        if (tokenPayload) {
                            this.applyTokenPayload(tokenPayload)
                        }

                        resolve(response)
                    })
                    .catch((error) => {
                        if (!error.response) useNotificationStore().setErrors(error)
                        else if (error.response.data.errors) useNotificationStore().setErrors(error.response.data.errors)
                        else if (error.response.data.message)
                            useNotificationStore().setErrors({
                                message: error.response.data.message, 
                            })
                        else useNotificationStore().setErrors(error)

                        reject(error)
                    })
            })
        },

        async loginWithPasskey(challengeId: string, passkey: WebAuthnCredentialPayload) {
            return new Promise((resolve, reject) => {
                loginUserWithPasskey(challengeId, passkey)
                    .then((response) => {
                        const tokenPayload = extractTokenPayload(response.data)

                        if (tokenPayload) {
                            this.applyTokenPayload(tokenPayload)

                        }

                        resolve(response)
                    })
                    .catch((error) => {
                        if (!error.response) useNotificationStore().setErrors(error)
                        else if (error.response.data.errors) useNotificationStore().setErrors(error.response.data.errors)
                        else if (error.response.data.message)
                            useNotificationStore().setErrors({
                                message: error.response.data.message,
                            })
                        else useNotificationStore().setErrors(error)

                        reject(error)
                    })
            })
        },

        async loginWithToken(token: string) {
            return new Promise((resolve, reject) => {
                loginUserWithToken(token)
                    .then((response) => {
                        const tokenPayload = extractTokenPayload(response.data)

                        if (tokenPayload) {
                            this.applyTokenPayload(tokenPayload)
                        }

                        resolve(response)
                    })
                    .catch((error) => {
                        if (!error.response) useNotificationStore().setErrors(error)
                        else if (error.response.data.errors) useNotificationStore().setErrors(error.response.data.errors)
                        else if (error.response.data.message)
                            useNotificationStore().setErrors({
                                message: error.response.data.message, 
                            })
                        else useNotificationStore().setErrors(error)

                        reject(error)
                    })
            })
        },

        async confirmLoginToken(token: string) {
            return new Promise((resolve, reject) => {
                confirmLoginToken(token)
                    .then((response) => resolve(response))
                    .catch((error) => {
                        if (!error.response) useNotificationStore().setErrors(error)
                        else if (error.response.data.errors) useNotificationStore().setErrors(error.response.data.errors)
                        else if (error.response.data.message)
                            useNotificationStore().setErrors({
                                message: error.response.data.message, 
                            })
                        else useNotificationStore().setErrors(error)

                        reject(error)
                    })
            })
        },

        askForPassword(email: string) {
            return askForNewPassword(email)
        },

        setPassword(password: string, passwordConfirmation: string, token: string) {
            return resetPassword(password, passwordConfirmation, token)
        },

        async refreshAccessToken() {
            return refreshAccessTokenRequest()
                .then((response) => {
                    if (response && response.data?.data) {
                        this.setTokens(
                            response.data.data.access_token,
                            response.data.data.refresh_token,
                            response.data.data.expires_in ?? null
                        )

                    }

                    this.refreshing = false

                    return response
                })
                .catch((error) => {
                    this.logout()

                    return Promise.reject(error)
                })
        },

        logout(message: string | null = null) {
            this.setTokens(null, null, null)
            this.setTicket(null)

            useVerificationStore().removeVerificationUuid()

            useAppStore().$reset()
            useUserStore().$reset()
            this.$reset()
            useBrandStore().$reset()
            useFilterStore().$reset()
            useNotificationStore().$reset()
            useModuleStore().$reset()
            useMenuStore().$reset()
            useVerificationStore().$reset()

            const currentPath = window.location.pathname
            const current = currentPath + window.location.search + window.location.hash
            const authPaths = ['/login', '/logout', '/forgotten-password', '/reset-password']
            const isOnAuthPage = authPaths.some(path => currentPath === path || currentPath.startsWith(path + '/'))

            if (!isOnAuthPage) {
                router.push({
                    path: '/login',
                    query: {
                        intended: current,
                        ...(message ? {
                            message,
                        } : {
                        }),
                    },
                })
            }
        },
    },
})

function extractTokenPayload(
    responseData: LoginUserResponseData
): { accessToken: string; refreshToken: string; expiresIn?: number; verificationUuid?: string } | null {
    const payload = (responseData as LoginUserDataResponse)?.data ?? responseData

    if (
        payload &&
        typeof payload === 'object' &&
        'access_token' in payload &&
        'refresh_token' in payload
    ) {
        /* eslint-disable camelcase */
        const tokenResponse = payload as {
            access_token: string
            refresh_token: string
            expires_in?: number
            verification_uuid?: string
        }
        const { access_token: accessToken, refresh_token: refreshToken, expires_in: expiresIn, verification_uuid: verificationUuid } = tokenResponse
        /* eslint-enable camelcase */

        return {
            accessToken,
            refreshToken,
            expiresIn,
            verificationUuid,
        }
    }

    return null
}
